/* Common Imports */

import React from "react";
import { styled, useTheme } from "@mui/system";

/* Redux & Type Imports */

import { NextRouter } from "next/router";
import StateInterface from "../../../redux-magic/state-interface";
import { setBroadcastFormThunk } from "../../../redux-magic/store";

/* Component Imports */

import { CircularProgress, MenuItem, TextField, Typography, SvgIcon } from "@mui/material";

/* Icon Imports */

import { EmojiObjectsOutlined } from "@mui/icons-material";
import { ObjectId } from "bson";
import AgentIcon from "../../../public/images/broadcast/agentsIcon.svg";
import {
	BroadcastPricingPayload,
	broadcastReach,
	defaultBroadcastBand,
	fetchBroadcastPricing,
} from "../../../lib/broadcasts/broadcastCheckout";
import { formatBreakdownAmount } from "../../../lib/payments/priceBreakdownPayload";

/* Styled Components */

const Container = styled("div")({
	display: "flex",
	flexDirection: "column",
	justifyContent: "start",
	alignItems: "center",
	width: "100%",
	height: "100%",
	minHeight: "100%",
	flex: 1,
	position: "relative",
});

const HeaderSection = styled("div")({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "space-between",
	backgroundColor: "#621B16",
	width: "100%",
	// height: "50%",
	padding: "2rem 0rem 0rem 0rem",
	gap: "0.5rem",
});

const Title = styled(Typography)({
	fontWeight: 700,
	fontSize: "1.25rem",
	textAlign: "center",
	color: "#FFFFFF",
});

const SubTitle = styled(Typography)({
	color: "#FFFFFF",
	fontWeight: 400,
	fontSize: "0.875rem",
	textAlign: "center",
});

const BroadcastBox = styled("div")({
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	width: "100%",
	borderRadius: "1rem",
	margin: "3rem auto 3rem auto",
	gap: "0.75rem",
});

const BroadcastLabel = styled(Typography)({
	fontWeight: 700,
	fontSize: "0.875rem",
	textAlign: "center",
});

const BroadcastNumber = styled(Typography)({
	color: "#FCB24F",
	fontWeight: 700,
	WebkitTextStroke: "2px #000",
	textShadow: `
        -1px -1px 0 #000,  
        1px -1px 0 #000,
        -1px  1px 0 #000,
        1px  1px 0 #000
    `,
	textAlign: "center",
	fontSize: "6rem",
	lineHeight: "6rem",
});

const TipContainer = styled("div")({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "flex-start",
	borderRadius: "1rem",
	background: "#FFF3E0",
	gap: "0.5rem",
	padding: "1rem",
	margin: "0rem 1rem 2rem 1rem",
});

const Tip = styled(Typography)({
	fontWeight: 400,
	fontSize: "0.75rem",
});

const AgentContainer = styled("div")({
	width: "100%",
	height: "100%",
	justifyContent: "center",
	alignItems: "end",
	display: "flex",
});

export default function NoOfAgentScreen({
	dispatch,
	router,
	broadcast,
	userId,
	business_profile_id,
	city = null,
}: {
	dispatch: Function;
	router: NextRouter;
	broadcast: StateInterface["broadcast_data"] | undefined | null;
	userId: string | ObjectId;
	business_profile_id: string | ObjectId | undefined | null;
	/* A broadcast to a city (4 Nov 2026): the count, the bands and the default band are that city's. */
	city?: string | null;
}) {
	const theme = useTheme();

	/* Translations */

	const localLang = {
		days: router.locale === "ar-AE" ? "يوم " : router.locale === "kn-IN" ? " ದಿನಗಳು" : " days",
		beegru_broadcast:
			router.locale === "ar-AE" ? "بث بيجرو" : router.locale === "kn-IN" ? "ಬೀಗ್ರು ಪ್ರಸಾರ" : "Beegru Broadcast",
		free_for_a_limited_time:
			router.locale === "ar-AE"
				? "مجاني لفترة محدودة"
				: router.locale === "kn-IN"
					? "ಮಿತಿಯಲ್ಲಿನ ಉಚಿತ"
					: "Free for a limited time",
		your_broadcast_will_reach:
			router.locale === "ar-AE"
				? "سيصل بثك إلى"
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಪ್ರಸಾರವು ತಲುಪುತ್ತದೆ"
					: "Your broadcast will reach",
		agents_on_beegru:
			router.locale === "ar-AE"
				? "وكلاء على بيجرو"
				: router.locale === "kn-IN"
					? "ಬೀಗ್ರುದಲ್ಲಿ ಏಜೆಂಟ್‌ಗಳು"
					: "agents on Beegru",
		/* Store billing (1 Nov 2026): the free broadcasts left, and the reach band a paid one buys. */
		free_broadcasts_left: (remaining: number, limit: number) =>
			router.locale === "ar-AE"
				? `${remaining} من ${limit} عمليات بث مجانية متبقية`
				: router.locale === "kn-IN"
					? `${limit} ಉಚಿತ ಪ್ರಸಾರಗಳಲ್ಲಿ ${remaining} ಉಳಿದಿವೆ`
					: `${remaining} of ${limit} free broadcasts left`,
		pick_your_reach:
			router.locale === "ar-AE"
				? "اختر عدد الوكلاء الذين تريد الوصول إليهم"
				: router.locale === "kn-IN"
					? "ಎಷ್ಟು ಏಜೆಂಟ್‌ಗಳನ್ನು ತಲುಪಬೇಕು ಎಂದು ಆಯ್ಕೆಮಾಡಿ"
					: "Pick how many agents to reach",
		reach: router.locale === "ar-AE" ? "الوصول" : router.locale === "kn-IN" ? "ತಲುಪುವಿಕೆ" : "Reach",
		agents: router.locale === "ar-AE" ? "وكلاء" : router.locale === "kn-IN" ? "ಏಜೆಂಟ್‌ಗಳು" : "agents",
		all: router.locale === "ar-AE" ? "جميع" : router.locale === "kn-IN" ? "ಎಲ್ಲಾ" : "All",
		tip_content:
			router.locale === "ar-AE"
				? "تمكنك ميزة البث من تنشيط شبكة Beegru من الوكلاء، مما يتيح لك الوصول إلى جمهور أوسع وإغلاق الصفقات بشكل أسرع."
				: router.locale === "kn-IN"
					? "ಬೀಗ್ರು ಪ್ರಸಾರ ವೈಶಿಷ್ಟ್ಯವು ನಿಮ್ಮನ್ನು ಬೀಗ್ರು ಏಜೆಂಟ್‌ಗಳ ಜಾಲವನ್ನು ಸಕ್ರಿಯಗೊಳಿಸಲು ಅನುಮತಿಸುತ್ತದೆ, ಇದರಿಂದ ನೀವು ವ್ಯಾಪಕ ಪ್ರೇಕ್ಷಕರಿಗೆ ತಲುಪಬಹುದು ಮತ್ತು ಒಪ್ಪಂದಗಳನ್ನು ವೇಗವಾಗಿ ಮುಗಿಸಬಹುದು"
					: "The broadcast feature enables you to activate Beegru's network of agents, helping you to reach a wider audience and close deals faster",
	};

	const [loadingAgents, setLoadingAgents] = React.useState<boolean>(false);
	/* The agents this business can reach, and its broadcast pricing: free broadcasts left, the bands it can buy. */
	const [pool, setPool] = React.useState<number>(0);
	const [pricing, setPricing] = React.useState<BroadcastPricingPayload | null>(null);
	const isPaid = pricing?.payment_enabled === true && pricing.is_free === false;

	/** Puts [noOfAgents] (a band, or a free broadcast's reach) on the broadcast being written. */
	const setNoOfAgents = (noOfAgents: number) =>
		dispatch(
			setBroadcastFormThunk({
				commission_offered: broadcast?.commission_offered || 0,
				no_of_agents: noOfAgents,
				notes: broadcast?.notes || "",
			}),
		);

	React.useEffect(() => {
		const url = `${process.env.PRODUCTION_API_URL}broadcast/fetch-no-of-agents`;

		setLoadingAgents(true);

		fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "text/plain",
			},
			body: JSON.stringify({
				creator_user_id: userId,
				creator_business_profile_id: business_profile_id,
				...(city ? { city } : {}),
			}),
		})
			.then((res) => res.json())
			.then(async (data) => {
				if (data.status === "success") {
					const count: number = data.payload || 0;
					// A paid broadcast buys a band (the largest offered by default); a free one reaches everyone up to
					// the largest band. The server re-checks both when it is sent.
					const readPricing = await fetchBroadcastPricing(business_profile_id ? String(business_profile_id) : "", 0, city || null);
					const band = defaultBroadcastBand(readPricing);
					setPool(count);
					setPricing(readPricing);
					setNoOfAgents(
						readPricing?.payment_enabled === true && readPricing.is_free === false && band
							? band.agents
							: broadcastReach(readPricing, count, 0),
					);
					setLoadingAgents(false);
				} else {
					console.error("Error fetching business data:", data.message);
					setLoadingAgents(false);
				}
			})
			.catch((err) => {
				console.error("Fetch error:", err);
				setLoadingAgents(false);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [city]);

	return (
		<Container>
			{/* Header */}

			<HeaderSection>
				<div>
					<Title variant="h6">{localLang.beegru_broadcast}</Title>
					<SubTitle variant="body2">
						{!pricing?.payment_enabled
							? localLang.free_for_a_limited_time
							: pricing.is_free && pricing.free_allowance
								? localLang.free_broadcasts_left(pricing.free_allowance.remaining, pricing.free_allowance.limit)
								: localLang.pick_your_reach}
					</SubTitle>
				</div>
				<AgentContainer>
					<SvgIcon
						component={AgentIcon}
						inheritViewBox={false}
						viewBox={"0 0 360 157"}
						fill="none"
						sx={{
							width: "80%",
							height: "100%",
							color: "#FFFFFF",
							/* 1080p breakpoint */
							[theme.breakpoints.up("xl")]: {
								width: "100%",
							},
						}}
					/>
				</AgentContainer>
			</HeaderSection>

			{/* No. of Agents */}

			<BroadcastBox>
				<BroadcastLabel variant="body2">{localLang.your_broadcast_will_reach}</BroadcastLabel>
				{loadingAgents || pool === 0 ? (
					<CircularProgress
						sx={{
							color: "#FCB24F",
						}}
						size={96}
					/>
				) : (
					<BroadcastNumber variant="h1">{broadcastReach(pricing, pool, broadcast?.no_of_agents || 0)}</BroadcastNumber>
				)}

				<BroadcastLabel variant="body2">{localLang.agents_on_beegru}</BroadcastLabel>

				{/* A paid broadcast picks its reach band: each one's price, GST included, the platform fee and any offer. */}
				{isPaid && pricing && pricing.bands.length > 0 ? (
					<TextField
						select
						size="small"
						label={localLang.reach}
						value={String(broadcast?.no_of_agents || defaultBroadcastBand(pricing)?.agents || "")}
						onChange={(event) => setNoOfAgents(Number(event.target.value))}
						sx={{ minWidth: "16rem", marginTop: "1rem" }}
					>
						{pricing.bands.map((band) => (
							<MenuItem
								key={band.agents}
								value={String(band.agents)}
							>
								{(band.agents > pool ? `${localLang.all} ${pool}` : String(band.agents)) +
									" " +
									localLang.agents +
									", " +
									formatBreakdownAmount(band.breakdown.total, band.breakdown.currency)}
							</MenuItem>
						))}
					</TextField>
				) : null}
			</BroadcastBox>

			{/* Tip */}

			<TipContainer>
				<EmojiObjectsOutlined
					sx={{
						color: "#FFB800",
						fontSize: "1.5rem",
					}}
				/>
				<Tip variant="body1">{localLang.tip_content}</Tip>
			</TipContainer>
		</Container>
	);
}
